import styled from 'styled-components';
import {
  getColorBrightness,
  getDarkColor,
  prependHashIfNeeded
} from '../../../functions/primitiveHelpers/colorHelpers';
import { useLazyLoadRoleIcon } from '../../../hooks/useLazyLoadIcon';
import THEME, { COLOR } from '../../../styles/colors';
import Font from '../../../styles/font';
import Icon, { IconType as IconModuleIconType } from '../../../ui/Icon/Icon';
import Text from '../../../ui/Text/StyledText';
import Anchor from '../../Anchor/Anchor';
import Mascot from '../../Image/Mascot';
import PhotoOrInitial from '../../Image/PhotoOrInitial';

// TODO: This logic is repeated (with some variation) throughout the codebase. We should consolidate.
export const getFallbackColor = (color1, color2, color3) => {
  const colorsBrightness = getColorBrightness([color1, color2, color3]);
  return prependHashIfNeeded(
    getDarkColor(colorsBrightness)?.color ?? THEME.brand.maxpreps
  );
};

const StyledListItem = styled.li`
  & > a {
    padding: 8px 0;
    display: flex;
    align-items: center;
    justify-content: flex-start;

    & .text-container {
      padding-left: 12px;

      & .header {
        color: ${COLOR.black};
        margin: 0 0 4px;
      }

      & .subheader {
        color: ${COLOR.neutral30};
      }
    }

    &:hover .header {
      color: inherit;
    }
  }
`;

interface IRoleIconProps {
  iconId: string;
  size: number;
}

const RoleIcon: React.FC<IRoleIconProps> = ({ iconId, size }) => {
  const icon = useLazyLoadRoleIcon(iconId);

  if (!icon) return null;

  return <Icon icon={icon} iconType={IconModuleIconType.Role} size={size} />;
};

export enum IconType {
  None,
  Team,
  Career,
  Role
}

interface IItemIconProps {
  type: IconType;
  iconSize: number;
  iconName: string;
}

interface IPhotoOrInitial {
  iconUrl: string;
  fallbackColor?: string;
}
interface ICareerIconProps {
  iconUrl: string;
}

interface IIconId {
  iconId: string;
}
type RoleIconProps = IPhotoOrInitial | IIconId;

type ItemIconProps = IItemIconProps &
  (IPhotoOrInitial | ICareerIconProps | RoleIconProps);

const ItemIcon: React.FC<Omit<ItemIconProps, 'children'>> = ({
  type,
  iconSize,
  iconName,
  ...rest
}) => {
  const { fallbackColor = THEME.brand.maxpreps, iconUrl } =
    rest as IPhotoOrInitial;
  switch (type) {
    case IconType.Team: {
      return (
        <Mascot
          src={iconUrl}
          size={iconSize}
          name={iconName}
          backgroundColor={fallbackColor}
          isLazyLoaded={false}
        />
      );
    }
    case IconType.Career: {
      const { iconUrl } = rest as ICareerIconProps;
      return (
        <PhotoOrInitial
          src={iconUrl}
          size={iconSize}
          alt={iconName}
          isLazyLoaded={false}
          fallbackLetter={iconName}
          backgroundColor={fallbackColor}
        />
      );
    }
    case IconType.Role: {
      const _rest = rest as IIconId;

      if (_rest.iconId) {
        return <RoleIcon iconId={_rest.iconId} size={iconSize} />;
      }

      const { fallbackColor, iconUrl } = rest as IPhotoOrInitial;
      return (
        <PhotoOrInitial
          src={iconUrl}
          size={iconSize}
          alt={iconName}
          fallbackLetter={iconName}
          backgroundColor={fallbackColor}
          isLazyLoaded={false}
        />
      );
    }
    default:
      return null;
  }
};

interface IListItemProps {
  type: IconType;
  header: string;
  subheader: string;
  href: string;
}

const ListItem: React.FC<IListItemProps & ItemIconProps> = ({
  header,
  subheader,
  href,
  type,
  ...rest
}) => (
  <StyledListItem>
    <Anchor
      href={href}
      $color={THEME.text.primary}
      $hoverColor={THEME.brand.maxpreps}
    >
      <ItemIcon type={type} {...rest} />
      <div className="text-container">
        <Text as="div" className={`header ${Font.f14_bold_tall}`}>
          {header}
        </Text>
        <Text as="div" className={`subheader ${Font.f14_tall}`}>
          {subheader}
        </Text>
      </div>
    </Anchor>
  </StyledListItem>
);

export default ListItem;
